import React, { useState, useEffect, useCallback, useMemo } from "react";
import CustomButton from "components/button";
import SelectInput, { DynamicOption } from "components/fields/SelectInput";
import DateInput from "../fields/DateInput";
import { reqFunction } from "utils/API";
import { renderToast } from "utils/globalUtils";
import { FiRotateCcw, FiXCircle } from "react-icons/fi";

// --- Utility Functions & Types ---

interface Order {
  id: string;
  order_type_code: string; // "001"
  order_type_id: string; // UUID (for retry)
  execute_time: string | null;
  created_at: string;
  status: "Pending" | "Done" | "Failed" | "Scheduled";
  command_name: string;
  command_param_display: any;
  command_param_ui_data: any;
  command_param_raw_obj: any; // keep object form for retry
}

interface WaterMeterOrdersManagerProps {
  deviceSelectData?: DynamicOption[] | null;
  orderTypeSelectData?: DynamicOption[] | null;
  onClose: () => void;
  device?: DynamicOption | null;
  setDevice?: React.Dispatch<React.SetStateAction<DynamicOption | null>>;
  orderType?: DynamicOption | null;
  setOrderType?: React.Dispatch<React.SetStateAction<DynamicOption | null>>;
  updateTable?: (options?: any) => Promise<any>;
}

// Helpers
const extractCommandCodeFromLabel = (label?: string) => {
  if (!label) return "";
  // label is: "001 - name"
  const code = label.split("-")[0]?.trim();
  return code || "";
};

// --- Command Definitions & Utilities ---

interface CommandDefinition {
  name_fa: string;
  default_param: any; // JS Object for default values
  ui_component_type:
    | "STATUS"
    | "SELECT"
    | "DATE"
    | "TEXT"
    | "DATETIME"
    | "PASSWORD"
    | "NUMBER"
    | "NONE";
  // For SELECT type: { key: value } mapping for options
  options?: { [key: string]: string };
  // For Date/Time commands (004, 007): Define expected keys
  param_keys?: string[];
}

// 1. Define ALL 9 Commands with UI hints
const FULL_COMMAND_MAP: { [key: string]: CommandDefinition } = {
  "001": {
    name_fa: "باز و بسته کردن رله",
    default_param: { status: 0 },
    ui_component_type: "SELECT",
    options: { "0": "بسته", "1": "باز" },
  },
  "002": {
    name_fa: "تنظیم زمان لاگ گیری",
    default_param: { interval: 30 },
    ui_component_type: "SELECT",
    options: { "30": "30 دقیقه", "45": "45 دقیقه", "60": "60 دقیقه" },
  },
  "003": {
    name_fa: "تغییر عدد تجمعی",
    default_param: { value: 1 },
    ui_component_type: "NUMBER", // Expecting a number input for 'value'
  },
  "004": {
    name_fa: "ارسال لاگ‌",
    default_param: {
      start_dt: "01/01/2024-00:00:00",
      end_dt: "12/31/2024-23:59:59",
    },
    ui_component_type: "DATETIME", // Requires two date/time inputs
    param_keys: ["start_dt", "end_dt"],
  },
  "005": {
    name_fa: "تغییر رمز عبور اول",
    default_param: { password: 12345678 },
    ui_component_type: "PASSWORD",
  },
  "006": {
    name_fa: "تعداد تلاش GSM",
    default_param: { new_retries: 10 },
    ui_component_type: "NUMBER",
  },
  "007": {
    name_fa: "تنظیم زمان ارسال",
    default_param: { time: "120000" }, // HHMMSS format
    ui_component_type: "TEXT", // Custom Time Input needed, using TEXT as fallback
  },
  "008": {
    name_fa: "ریست",
    default_param: {},
    ui_component_type: "NONE",
  },
  "009": {
    name_fa: "حذف حافظه",
    default_param: {},
    ui_component_type: "NONE",
  },
};

// --- Parameter Display & Parsing Functions ---

type DisplayResult = {
  display: any;
  uiData: any;
};

// Function to format parameter for Table display (command_param_display)
const formatParamForDisplay = (
  commandId: string,
  paramJson: string
): DisplayResult => {
  try {
    const paramObj = JSON.parse(paramJson);
    const def = FULL_COMMAND_MAP[commandId];

    if (!def) {
      return { display: paramJson, uiData: paramObj };
    }

    switch (def.ui_component_type) {
      case "STATUS": // Used for 001
      case "SELECT":
        const statusValue =
          paramObj.status !== undefined
            ? paramObj.status
            : paramObj.interval || paramObj.value;
        const mappedValue = def.options?.[String(statusValue)];
        return {
          display: mappedValue
            ? `${mappedValue}`
            : `مقدار: ${statusValue || "نامشخص"}`,
          uiData: paramObj,
        };

      case "NUMBER":
        const numKey = Object.keys(paramObj)[0];
        return {
          display: `${paramObj[numKey] || ""} لیتر`,
          uiData: paramObj,
        };

      case "DATETIME":
        const start = paramObj.start_dt || "N/A";
        const end = paramObj.end_dt || "N/A";
        // Simplistic display for dates; real parsing/formatting would need date-fns or manual logic
        return {
          display: (
            <>
              از تاریخ: {start}
              <br />
              تا تاریخ: {end}
            </>
          ),
          uiData: paramObj,
        };

      case "PASSWORD":
        return {
          display: `رمز جدید تنظیم شد (تایید شده)`,
          uiData: paramObj,
        };

      case "TEXT": // For 007 Time
        const timeVal = paramObj.time || "N/A";
        return {
          display: `زمان تنظیم شده: ${timeVal.substring(
            0,
            2
          )}:${timeVal.substring(2, 4)}:${timeVal.substring(4, 6)}`,
          uiData: paramObj,
        };

      default:
        return { display: paramJson, uiData: paramObj };
    }
  } catch (e) {
    console.error("Error parsing command param:", e);
    return { display: paramJson, uiData: {} };
  }
};

// Function to get Command Name based on ID
const getCommandName = (commandId: string): string => {
  return (
    FULL_COMMAND_MAP[commandId]?.name_fa || `Unknown Command (${commandId})`
  );
};

// --- Mock Data Setup (Adapted to use the new structure) ---

// const MOCK_ORDERS: Order[] = [
//   {
//     id: "M001",
//     order_type: "001",
//     execute_time: new Date(Date.now() + 86400000).toISOString(),
//     created_at: new Date(Date.now() - 7200000).toISOString(),
//     status: "Scheduled",
//     command_name: getCommandName("001"),
//     command_param_raw: JSON.stringify(FULL_COMMAND_MAP["001"].default_param),
//     command_param_display: formatParamForDisplay(
//       "001",
//       JSON.stringify(FULL_COMMAND_MAP["001"].default_param)
//     ).display,
//     command_param_ui_data: FULL_COMMAND_MAP["001"].default_param,
//   },
//   {
//     id: "M008",
//     order_type: "008",
//     execute_time: new Date(Date.now() - 3600000).toISOString(),
//     created_at: new Date(Date.now() - 10800000).toISOString(),
//     status: "Done",
//     command_name: getCommandName("008"),
//     command_param_raw: JSON.stringify(FULL_COMMAND_MAP["008"].default_param),
//     command_param_display: formatParamForDisplay(
//       "008",
//       JSON.stringify(FULL_COMMAND_MAP["008"].default_param)
//     ).display,
//     command_param_ui_data: FULL_COMMAND_MAP["008"].default_param,
//   },
//   {
//     id: "M002",
//     order_type: "002",
//     execute_time: new Date(Date.now() - 3600000).toISOString(),
//     created_at: new Date(Date.now() - 10800000).toISOString(),
//     status: "Pending",
//     command_name: getCommandName("002"),
//     command_param_raw: JSON.stringify(FULL_COMMAND_MAP["002"].default_param),
//     command_param_display: formatParamForDisplay(
//       "002",
//       JSON.stringify(FULL_COMMAND_MAP["002"].default_param)
//     ).display,
//     command_param_ui_data: FULL_COMMAND_MAP["002"].default_param,
//   },
//   {
//     id: "M004",
//     order_type: "004",
//     execute_time: new Date(Date.now() - 3600000).toISOString(),
//     created_at: new Date(Date.now() - 10800000).toISOString(),
//     status: "Failed",
//     command_name: getCommandName("004"),
//     command_param_raw: JSON.stringify(FULL_COMMAND_MAP["004"].default_param),
//     command_param_display: formatParamForDisplay(
//       "004",
//       JSON.stringify(FULL_COMMAND_MAP["004"].default_param)
//     ).display,
//     command_param_ui_data: FULL_COMMAND_MAP["004"].default_param,
//   },
// ];

const getStatusBadge = (status: Order["status"]) => {
  switch (status) {
    case "Done":
      return (
        <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
          تکمیل شده
        </span>
      );
    case "Pending":
    case "Scheduled":
      return (
        <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">
          {status === "Pending" ? "در انتظار" : "زمان‌بندی شده"}
        </span>
      );
    case "Failed":
      return (
        <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">
          خطا
        </span>
      );
    default:
      return (
        <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800">
          {status}
        </span>
      );
  }
};

// --- Custom Parameter Input Component (Handles UI logic) ---
interface DynamicParamInputProps {
  commandId: string;
  paramData: any;
  setParamData: (data: any) => void;
}

const DynamicParamInput: React.FC<DynamicParamInputProps> = ({
  commandId,
  paramData,
  setParamData,
}) => {
  const definition = FULL_COMMAND_MAP[commandId];

  if (!definition || definition.ui_component_type === "NONE") {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        پارامتری برای تنظیم ندارد.
      </p>
    );
  }

  const handleInputChange = (key: string, value: any) => {
    setParamData({ ...paramData, [key]: value });
  };

  const renderInput = () => {
    switch (definition.ui_component_type) {
      case "STATUS":
      case "SELECT":
        const options = definition.options || {};
        const currentValue = paramData[Object.keys(paramData)[0]]; // Assuming single key for status/interval/value

        const selectOptions: DynamicOption[] = Object.entries(options).map(
          ([value, label]) => ({
            value: value,
            label: label,
          })
        );

        // Determine which key to use for state update (status, interval, or value)
        const stateKey = Object.keys(definition.default_param)[0];

        return (
          <SelectInput
            label={`انتخاب ${definition.name_fa}`}
            dynamicOptions={selectOptions}
            state={{
              value: String(currentValue),
              label: options[currentValue] || "",
            }}
            setState={(s: DynamicOption) =>
              handleInputChange(
                stateKey,
                s.value === "1"
                  ? 1
                  : s.value === "0"
                  ? 0
                  : Number(s.value) || s.value
              )
            }
            placeholder="انتخاب کنید"
          />
        );

      case "NUMBER":
        const numKey = Object.keys(definition.default_param)[0];
        return (
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              مقدار ({numKey})
            </label>
            <input
              type="number"
              value={paramData[numKey] || ""}
              onChange={(e) =>
                handleInputChange(numKey, parseFloat(e.target.value) || 0)
              }
              className="mt-1 block w-full rounded-lg border p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              واحد: {numKey === "value" ? "لیتر" : ""}
            </span>
          </div>
        );

      case "DATETIME":
        const keys = definition.param_keys || [];

        return (
          <div className="space-y-3 md:col-span-2">
            {keys.map((key) => (
              <DateInput
                key={key}
                label={key === "start_dt" ? "تاریخ شروع" : "تاریخ پایان"}
                value={paramData[key]}
                onChange={(v: any) => handleInputChange(key, v)}
                // isDateTime={true} // Assuming DateInput can handle full datetime
              />
            ))}
          </div>
        );

      case "PASSWORD":
        const passKey = Object.keys(definition.default_param)[0];
        return (
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              رمز جدید
            </label>
            <input
              type="password"
              value={paramData[passKey] || ""}
              onChange={(e) => handleInputChange(passKey, e.target.value)}
              className="mt-1 block w-full rounded-lg border p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="رمز جدید را وارد کنید"
            />
          </div>
        );

      case "TEXT": // For 007 Time
        const timeKey = Object.keys(definition.default_param)[0];
        return (
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              زمان (HHMMSS)
            </label>
            <input
              type="text"
              value={paramData[timeKey] || ""}
              onChange={(e) =>
                handleInputChange(
                  timeKey,
                  e.target.value.replace(/[^0-9]/g, "").slice(0, 6)
                )
              }
              className="mt-1 block w-full rounded-lg border p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="مثال: 143000 (ساعت 14:30:00)"
            />
          </div>
        );

      default:
        return (
          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              پارامتر خام JSON
            </label>
            <textarea
              value={JSON.stringify(paramData)}
              onChange={(e) => setParamData(JSON.parse(e.target.value))}
              rows={3}
              className="w-full resize-none rounded-lg border p-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        );
    }
  };

  return <div className="col-span-3">{renderInput()}</div>;
};

// --- Main Component ---

const WaterMeterOrdersManager: React.FC<WaterMeterOrdersManagerProps> = ({
  onClose,
  orderTypeSelectData,
  deviceSelectData,
}) => {
  const [selectedDevice, setSelectedDevice] = useState<DynamicOption | null>(
    null
  );
  const [selectedOrderType, setSelectedOrderType] =
    useState<DynamicOption | null>(null);
  const [orderList, setOrderList] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingOrders, setIsFetchingOrders] = useState(false);
  const [newOrderExecuteTime, setNewOrderExecuteTime] = useState<any>(null);

  // State for dynamic UI input in the Create section
  const [commandParamUIData, setCommandParamUIData] = useState<any>({});

  // --- Action Handlers (Placeholders) ---

  const handleRetry = async (order: Order) => {
    if (!selectedDevice || !order.order_type_id) return;

    try {
      const response = await reqFunction("Order/admin/create", {
        order_type_id: order.order_type_id,
        water_meter_serial: selectedDevice.value,
        order_parameters: order.command_param_raw_obj || {},
        // execute_time
      });

      if (response.code === 200) {
        renderToast("تلاش مجدد ارسال شد.", "success");
        fetchOrders(selectedDevice.value as string);
      } else {
        renderToast(response?.farsi_message || "خطا در تلاش مجدد.", "err");
      }
    } catch {
      renderToast("خطا در اتصال برای تلاش مجدد.", "err");
    }
  };

  const handleCancel = async (order: Order) => {
    try {
      const response = await reqFunction("Order/admin/delete", {
        order_id: order.id,
      });

      if (response.code === 200) {
        renderToast("دستور با موفقیت حذف شد.", "success");
        if (selectedDevice) fetchOrders(selectedDevice.value as string);
      } else {
        renderToast(response?.farsi_message || "خطا در حذف دستور.", "err");
      }
    } catch {
      renderToast("ارتباط با سرور برای حذف دستور ناموفق بود.", "err");
    }
  };

  // 1. Fetch Orders (Updated to include command details if API returns them)
  const fetchOrders = useCallback(async (serialNumber: string) => {
    if (!serialNumber) {
      setOrderList([]);
      return;
    }
    setIsFetchingOrders(true);
    try {
      const response = await reqFunction("Order/current/list", {
        page: 1,
        count: 100,
        water_meter_serial: serialNumber,
      });

      if (response.code === 200 && Array.isArray(response.data)) {
        const orders: Order[] = response.data.map((item: any) => {
          const code = item?.order_type?.order_type_code || "008";
          const typeId = item?.order_type?.order_type_id || "";
          const rawObj = item?.order_parameters || {};
          const rawParamJson = JSON.stringify(rawObj);
          const { display, uiData } = formatParamForDisplay(code, rawParamJson);

          return {
            id: item.order_id,
            order_type_code: code,
            order_type_id: typeId,
            execute_time: item.order_execute_time || null,
            created_at: item.order_create_time,
            status: item.order_status || "Pending",
            command_name: getCommandName(code),
            command_param_display: display,
            command_param_ui_data: uiData,
            command_param_raw_obj: rawObj,
          };
        });

        setOrderList(orders);
      } else {
        setOrderList([]);
        renderToast(
          response?.farsi_message || "خطا در دریافت لیست دستورات.",
          "err"
        );
      }
    } catch (error) {
      setOrderList([]);
      renderToast("خطا در اتصال به API.", "err");
    } finally {
      setIsFetchingOrders(false);
    }
  }, []);

  // Effect to trigger order fetch when the device selection changes
  useEffect(() => {
    if (selectedDevice) {
      fetchOrders(selectedDevice.value as string);
      // Reset command creation state
      setSelectedOrderType(null);
      setCommandParamUIData({});
      setNewOrderExecuteTime(null);
    } else {
      setOrderList([]);
    }
  }, [selectedDevice, fetchOrders]);

  // Effect to initialize UI data when order type changes
  useEffect(() => {
    if (selectedOrderType) {
      const code = extractCommandCodeFromLabel(selectedOrderType.label);
      const definition = FULL_COMMAND_MAP[code];
      setCommandParamUIData(definition ? definition.default_param : {});
    } else {
      setCommandParamUIData({});
    }
  }, [selectedOrderType]);

  // 2. Create New Order
  const createNewOrder = async () => {
    if (!selectedDevice || !selectedOrderType) {
      renderToast("لطفاً دستگاه و نوع دستور را انتخاب کنید.", "warn");
      return;
    }

    const code = extractCommandCodeFromLabel(selectedOrderType.label);
    const definition = FULL_COMMAND_MAP[code];

    // validate required params if command expects params
    if (definition && definition.ui_component_type !== "NONE") {
      const requiredKeys =
        definition?.param_keys || Object.keys(definition?.default_param || {});
      const missing = requiredKeys.some((key) => {
        const v = commandParamUIData?.[key];
        if (v === null || v === undefined) return true;
        if (typeof v === "string" && !v.trim()) return true;
        return false;
      });

      if (missing) {
        renderToast(
          "لطفاً تمام پارامترهای مورد نیاز دستور را وارد نمایید.",
          "warn"
        );
        return;
      }
    }

    let formattedDate: string | undefined = undefined;
    if (newOrderExecuteTime) {
      const dateObject =
        newOrderExecuteTime instanceof Date
          ? newOrderExecuteTime
          : new Date(newOrderExecuteTime);
      formattedDate = dateObject.toISOString();
    }

    setIsLoading(true);
    try {
      const payload: any = {
        order_type_id: selectedOrderType.value,
        water_meter_serial: selectedDevice.value,
        order_parameters: commandParamUIData,
      };
      if (formattedDate) payload.execute_time = formattedDate;

      const response = await reqFunction("Order/admin/create", payload);

      if (response.code === 200) {
        renderToast("دستور با موفقیت ایجاد شد.", "success");
        fetchOrders(selectedDevice.value as string);

        setSelectedOrderType(null);
        setNewOrderExecuteTime(null);
        setCommandParamUIData({});
      } else {
        renderToast(
          response?.farsi_message || "خطا در ایجاد دستور جدید.",
          "err"
        );
      }
    } catch (error) {
      renderToast("ارتباط با سرور برای ایجاد دستور ناموفق بود.", "err");
    } finally {
      setIsLoading(false);
    }
  };

  // Memoized Table Headers
  const tableHeaders = useMemo(
    () => (
      <tr>
        <th className="border-b px-4 py-2 text-right dark:border-gray-700">
          شناسه دستور
        </th>
        <th className="border-b px-4 py-2 text-right dark:border-gray-700">
          نوع دستور
        </th>
        <th className="border-b px-4 py-2 text-right dark:border-gray-700">
          پارامتر
        </th>
        <th className="border-b px-4 py-2 text-right dark:border-gray-700">
          زمان ایجاد
        </th>
        <th className="border-b px-4 py-2 text-right dark:border-gray-700">
          زمان اجرا
        </th>
        <th className="border-b px-4 py-2 text-right dark:border-gray-700">
          وضعیت
        </th>
        <th className="border-b px-4 py-2 text-right dark:border-gray-700">
          عملیات
        </th>
      </tr>
    ),
    []
  );

  // Map orderTypeSelectData to match COMMAND_MAP IDs for selection consistency in Create section
  const availableOrderTypesForCreation: DynamicOption[] = useMemo(() => {
    if (!orderTypeSelectData) return [];

    return orderTypeSelectData
      .map((opt) => {
        const code = extractCommandCodeFromLabel(opt.label);
        const definition = FULL_COMMAND_MAP[code];
        if (definition) {
          return {
            value: opt.value, // UUID stays as value (for create)
            label: opt.label, // keep original label (no UI change)
          };
        }
        return null;
      })
      .filter(Boolean) as DynamicOption[];
  }, [orderTypeSelectData]);

  // --- UI Structure ---
  return (
    <div className="p-4">
      {/* SECTION 1: Device Selection */}
      <div className="mb-6 rounded-lg border bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700">
        <SelectInput
          label="انتخاب کنتور آب"
          placeholder="شماره سریال کنتور را انتخاب کنید"
          dynamicOptions={deviceSelectData}
          state={selectedDevice}
          setState={setSelectedDevice}
        />
      </div>

      {selectedDevice ? (
        <div className="space-y-8">
          {/* SECTION 2: Current Orders List */}
          <div className="rounded-lg border p-4 dark:border-gray-600">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold dark:text-white">
                لیست دستورات کنتور ({selectedDevice.label})
              </h3>
              <CustomButton
                onClick={() => fetchOrders(selectedDevice.value as string)}
                text={isFetchingOrders ? "در حال بارگذاری..." : "تازه‌سازی"}
                color="blue"
                disabled={isFetchingOrders}
              />
            </div>

            {isFetchingOrders ? (
              <p className="py-8 text-center dark:text-gray-300">
                در حال دریافت لیست دستورات...
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full leading-normal">
                  <thead>{tableHeaders}</thead>
                  <tbody>
                    {orderList.length > 0 ? (
                      orderList.map((order, index) => {
                        const canRetry =
                          order.status === "Done" || order.status === "Failed";
                        const canCancel =
                          order.status === "Pending" ||
                          order.status === "Scheduled";

                        return (
                          <tr
                            key={index}
                            className="hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <td className="border-b px-4 py-2 text-sm text-gray-900 dark:border-gray-600 dark:text-gray-200">
                              {order.order_type_code}
                            </td>
                            <td className="border-b px-4 py-2 text-sm text-gray-900 dark:border-gray-600 dark:text-gray-200">
                              {order.command_name}
                            </td>
                            <td className="max-w-xs border-b px-4 py-2 text-sm text-gray-900 dark:border-gray-600 dark:text-gray-200">
                              {order.command_param_display}
                            </td>
                            <td className="border-b px-4 py-2 text-sm text-gray-900 dark:border-gray-600 dark:text-gray-200">
                              {new Date(order.created_at).toLocaleString(
                                "fa-IR"
                              )}
                            </td>
                            <td className="border-b px-4 py-2 text-sm text-gray-900 dark:border-gray-600 dark:text-gray-200">
                              {order.execute_time
                                ? new Date(order.execute_time).toLocaleString(
                                    "fa-IR"
                                  )
                                : "-"}
                            </td>
                            <td className="border-b px-4 py-2 text-sm text-gray-900 dark:border-gray-600 dark:text-gray-200">
                              {getStatusBadge(order.status)}
                            </td>
                            <td className="whitespace-nowrap border-b px-4 py-2 text-sm dark:border-gray-600">
                              {canRetry && (
                                <CustomButton
                                  onClick={() => handleRetry(order)}
                                  color="blue"
                                  icon={<FiRotateCcw />}
                                  size="sm"
                                />
                              )}
                              {canCancel && (
                                <CustomButton
                                  onClick={() => handleCancel(order)}
                                  color="red"
                                  icon={<FiXCircle />}
                                  size="sm"
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-4 text-center text-gray-500 dark:text-gray-400"
                        >
                          هیچ دستوری برای این کنتور ثبت نشده است.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* SECTION 3: New Order Creation Form */}
          <div className="rounded-lg border-t-4 border-green-500 bg-white p-6 shadow-md dark:border-green-700 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-bold text-green-700 dark:text-green-400">
              ایجاد دستور جدید
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Order Type Selector (Uses filtered list based on COMMAND_MAP) */}
              <SelectInput
                label="نوع دستور"
                placeholder="دستور را انتخاب کنید"
                dynamicOptions={availableOrderTypesForCreation}
                state={selectedOrderType}
                setState={setSelectedOrderType}
              />

              {/* Date Input for Execution Time */}
              <DateInput
                label="زمان اجرای مورد نظر"
                value={newOrderExecuteTime}
                onChange={setNewOrderExecuteTime}
              />

              {/* Dynamic Parameter UI based on selected Order Type */}
              {selectedOrderType && (
                <DynamicParamInput
                  commandId={extractCommandCodeFromLabel(
                    selectedOrderType.label
                  )}
                  paramData={commandParamUIData}
                  setParamData={setCommandParamUIData}
                />
              )}
            </div>

            {/* Display Raw JSON if needed for debugging */}
            {/* <div className='mt-4 text-xs bg-gray-100 p-2 rounded dark:bg-gray-700 dark:text-gray-400'>
                ارسال به API: {commandParamInput}
            </div> */}

            <div className="mt-6 flex justify-end space-x-3">
              <CustomButton onClick={onClose} text="بستن" color="red" />

              <CustomButton
                onClick={createNewOrder}
                text={isLoading ? "در حال ارسال..." : "ایجاد دستور"}
                color="green"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-6 text-center dark:border-yellow-600 dark:bg-yellow-900/20">
          <p className="font-medium text-yellow-700 dark:text-yellow-300">
            لطفاً در بالای صفحه یک کنتور آب را انتخاب کنید تا بتوانید لیست
            دستورات آن را مشاهده و دستور جدید ایجاد نمایید.
          </p>
        </div>
      )}
    </div>
  );
};

export default WaterMeterOrdersManager;
